/**
 * 설명서 그림.
 *
 * 이미지 파일이 아니라 콘솔과 같은 색·글꼴로 그린다. 스크린샷은 화면을 조금만 고쳐도 틀린
 * 그림이 되고 누구도 다시 찍지 않는다. 대신 **흐름과 배치**만 그린다 — 단추의 정확한 모양은
 * 본문의 `[[단추]]` 가 맡는다.
 *
 * 좁은 화면에서는 가로로 늘어선 흐름을 세로로 세운다. 가로 스크롤로 흐름을 따라가게
 * 하는 것보다 위에서 아래로 읽는 편이 휴대폰에서 자연스럽다.
 */
import { Fragment, type ReactNode } from 'react';
import {
  Bell,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  FileText,
  Hand,
  LifeBuoy,
  ListMusic,
  Monitor,
  MonitorPlay,
  PackageOpen,
  RotateCcw,
  Search,
  ShieldAlert,
  Upload,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { FigureId } from './types.js';

/** 흐름 사이 화살표 — 넓으면 오른쪽, 좁으면 아래. */
function FlowArrow(): ReactNode {
  return (
    <span className="flex shrink-0 items-center justify-center text-slate-300" aria-hidden="true">
      <ChevronDown className="h-5 w-5 sm:hidden" strokeWidth={2.2} />
      <ChevronRight className="hidden h-5 w-5 sm:block" strokeWidth={2.2} />
    </span>
  );
}

function Marker({ n }: { n: number }): ReactNode {
  return (
    <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-th-5 text-[10px] font-bold leading-none text-[#1a2e05]">
      {n}
    </span>
  );
}

/* ── 처음 한 번의 순서 ─────────────────────────────────────────────── */

const QUICK_STEPS: ReadonlyArray<{ icon: LucideIcon; title: string; where: string }> = [
  { icon: PackageOpen, title: '기기 준비', where: '공급사 설치' },
  { icon: Monitor, title: '화면 등록', where: '화면 › 화면 추가' },
  { icon: FileText, title: '콘텐츠 올리기', where: '콘텐츠' },
  { icon: ListMusic, title: '플레이리스트', where: '플레이리스트' },
  { icon: CalendarDays, title: '편성 배정', where: '화면 상세' },
];

function QuickStart(): ReactNode {
  return (
    <ol className="flex flex-col items-stretch gap-1.5 sm:flex-row sm:items-center">
      {QUICK_STEPS.map((step, at) => {
        const Icon = step.icon;
        return (
          <Fragment key={step.title}>
            {at > 0 && <FlowArrow />}
            <li className="flex flex-1 items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 sm:flex-col sm:gap-2 sm:px-2 sm:py-4 sm:text-center">
              <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-th-3">
                <Icon className="h-5 w-5 text-th-6" strokeWidth={1.9} />
                <span className="absolute -right-1.5 -top-1.5">
                  <Marker n={at + 1} />
                </span>
              </span>
              <span>
                <span className="block text-[13px] font-semibold text-slate-900">{step.title}</span>
                <span className="block text-[11.5px] text-slate-500">{step.where}</span>
              </span>
            </li>
          </Fragment>
        );
      })}
    </ol>
  );
}

/* ── 콘솔 화면 구성 ────────────────────────────────────────────────── */

const WIRE_SECTIONS: ReadonlyArray<{ title: string; items: readonly string[] }> = [
  { title: 'MAIN', items: ['대시보드', '콘텐츠', '화면', '모니터링'] },
  { title: '운영', items: ['플레이리스트', '스케줄', '캠페인'] },
  { title: '관리', items: ['리포트', '사용자'] },
];

function ConsoleLayout(): ReactNode {
  return (
    <div className="mx-auto flex h-[300px] min-w-[520px] max-w-2xl overflow-hidden rounded-lg border border-slate-300 bg-white text-[11px] shadow-sm">
      <div className="flex w-36 shrink-0 flex-col bg-th-3 px-2 py-2.5 text-slate-400">
        <div className="mb-2 flex items-center gap-1.5 px-1">
          <span className="flex h-5 w-5 items-center justify-center rounded bg-th-5">
            <MonitorPlay className="h-3 w-3 text-[#1a2e05]" />
          </span>
          <span className="font-semibold text-white">SignageOne</span>
        </div>
        <div className="flex items-center gap-1 px-1">
          <Marker n={1} />
          <span className="text-[9.5px] text-slate-500">매일 쓰는 메뉴</span>
        </div>
        {WIRE_SECTIONS.map((section) => (
          <div key={section.title} className="mt-1.5">
            <p className="px-1 text-[8.5px] font-semibold tracking-wider text-slate-500">{section.title}</p>
            {section.items.map((item, at) => (
              <p
                key={item}
                className={`truncate rounded px-1.5 py-[1px] ${section.title === 'MAIN' && at === 0 ? 'bg-th-5/20 text-white' : ''}`}
              >
                {item}
              </p>
            ))}
          </div>
        ))}
        <div className="mt-auto space-y-0.5 border-t border-white/10 pt-1.5">
          <p className="flex items-center gap-1 rounded bg-th-5/25 px-1.5 py-[2px] font-medium text-white ring-1 ring-th-5">
            <LifeBuoy className="h-3 w-3" />
            사용 설명서
            <span className="ml-auto">
              <Marker n={2} />
            </span>
          </p>
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-11 items-center gap-2 border-b border-slate-200 px-3">
          <span className="font-semibold text-slate-800">대시보드</span>
          <span className="ml-auto flex w-40 items-center gap-1 rounded border border-slate-200 px-1.5 py-1 text-slate-400">
            <Search className="h-3 w-3" />
            콘텐츠, 화면 검색…
            <span className="ml-auto rounded border border-slate-200 px-1 text-[9px]">/</span>
          </span>
          <Marker n={3} />
          <span className="relative">
            <Bell className="h-4 w-4 text-slate-500" />
            <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-red-500" />
          </span>
          <Marker n={4} />
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-th-3 text-[9px] font-semibold text-white">관</span>
          <Marker n={5} />
        </div>
        <div className="flex-1 space-y-2 bg-th-1 p-3">
          <div className="grid grid-cols-4 gap-2">
            {['온라인 화면', '활성 콘텐츠', '플레이리스트', '주의 필요'].map((label) => (
              <div key={label} className="rounded border border-slate-200 bg-white p-1.5">
                <div className="h-3 w-6 rounded bg-slate-200" />
                <p className="mt-1 truncate text-[9px] text-slate-400">{label}</p>
              </div>
            ))}
          </div>
          <div className="grid h-[150px] grid-cols-3 gap-2">
            <div className="col-span-2 rounded border border-slate-200 bg-white p-2">
              <div className="h-2 w-16 rounded bg-slate-200" />
              {[0, 1, 2, 3].map((row) => (
                <div key={row} className="mt-2.5 flex items-center gap-1.5">
                  <span className={`h-1.5 w-1.5 rounded-full ${row === 1 ? 'bg-slate-300' : 'bg-green-500'}`} />
                  <div className="h-1.5 flex-1 rounded bg-slate-100" />
                </div>
              ))}
            </div>
            <div className="rounded border border-slate-200 bg-white p-2">
              <div className="mx-auto mt-3 h-14 w-14 rounded-full border-[6px] border-th-3/70 border-r-th-5 border-t-th-6" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── 편성 우선순위 ─────────────────────────────────────────────────── */

const LAYERS: ReadonlyArray<{ label: string; note: string; className: string; width: string }> = [
  { label: '긴급 오버라이드', note: '모든 화면 · 모든 것보다 먼저', className: 'bg-red-600 text-white', width: 'sm:w-[64%]' },
  { label: '바로 보내기', note: '고른 화면에만 임시로', className: 'bg-amber-500 text-white', width: 'sm:w-[71%]' },
  { label: '캠페인', note: '기간·대상을 정해 덮어쓰기 · 끼워 넣기', className: 'bg-blue-600 text-white', width: 'sm:w-[79%]' },
  { label: '화면에 배정한 편성', note: '스케줄이면 시간대 규칙 → 평소 편성', className: 'bg-th-3 text-white', width: 'sm:w-[86%]' },
  { label: '그룹에 배정한 편성', note: '화면에 배정한 것이 없을 때', className: 'bg-slate-500 text-white', width: 'sm:w-[93%]' },
  { label: '대기 화면', note: '아무것도 걸리지 않을 때', className: 'bg-slate-200 text-slate-600', width: 'sm:w-full' },
];

function Priority(): ReactNode {
  return (
    <div className="mx-auto flex max-w-2xl gap-3">
      <div className="flex w-6 shrink-0 flex-col items-center text-[11px] font-medium text-slate-500">
        <span>강</span>
        <span className="my-1 w-px flex-1 bg-gradient-to-b from-red-400 to-slate-200" />
        <span>약</span>
      </div>
      <ol className="flex flex-1 flex-col items-center gap-1.5">
        {LAYERS.map((layer, at) => (
          <li
            key={layer.label}
            className={`flex w-full flex-col rounded-lg px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 ${layer.className} ${layer.width}`}
          >
            <span className="text-[13px] font-semibold">
              <span className="mr-1.5 opacity-70">{at + 1}</span>
              {layer.label}
            </span>
            <span className="text-[11.5px] opacity-85">{layer.note}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ── 설치 흐름 ─────────────────────────────────────────────────────── */

function FlowColumn({ step, where, children }: { step: number; where: string; children: ReactNode }): ReactNode {
  return (
    <div className="flex flex-1 flex-col">
      <p className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-slate-700">
        <Marker n={step} />
        {where}
      </p>
      <div className="flex flex-1 flex-col justify-center rounded-lg border border-slate-200 bg-white p-3">{children}</div>
    </div>
  );
}

function MockButton({ children, primary = false }: { children: ReactNode; primary?: boolean }): ReactNode {
  return (
    <span
      className={`inline-flex items-center justify-center rounded-md border px-2 py-1 text-[11.5px] font-medium ${
        primary ? 'border-th-3 bg-th-3 text-white' : 'border-slate-300 bg-white text-slate-700'
      }`}
    >
      {children}
    </span>
  );
}

function MockField({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <p className="text-[10.5px] text-slate-500">{label}</p>
      <p className="mt-0.5 rounded border border-slate-300 bg-white px-1.5 py-1 font-mono text-[11.5px] text-slate-800">{value}</p>
    </div>
  );
}

function InstallFlow(): ReactNode {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
      <FlowColumn step={1} where="공급사 · 에이전트 설치">
        <div className="flex items-center gap-2.5 py-2">
          <PackageOpen className="h-8 w-8 shrink-0 text-th-3" strokeWidth={1.6} />
          <p className="text-[11.5px] leading-snug text-slate-600">화면으로 쓸 PC·TV 박스에 재생 프로그램을 깔아 둡니다</p>
        </div>
      </FlowColumn>
      <FlowArrow />
      <FlowColumn step={2} where="화면 기기">
        <div className="rounded-md border-4 border-slate-800 bg-slate-900 px-2 py-4 text-center">
          <p className="text-[10px] text-slate-400">설치 후 실행하면</p>
          <p className="mt-1 font-mono text-[22px] font-semibold tracking-[0.2em] text-white">H7KP3W</p>
          <p className="mt-1 text-[9.5px] text-slate-400">[화면 추가]에 이 코드를 입력하세요</p>
        </div>
        <div className="mx-auto h-2 w-10 rounded-b bg-slate-300" />
      </FlowColumn>
      <FlowArrow />
      <FlowColumn step={3} where="콘솔 · 화면 추가">
        <div className="space-y-1.5">
          <MockField label="등록 코드" value="H7KP3W" />
          <MockField label="화면 이름" value="본사 1층 로비" />
        </div>
        <div className="mt-2 text-right">
          <MockButton primary>등록</MockButton>
        </div>
      </FlowColumn>
    </div>
  );
}

/* ── 롤아웃 ────────────────────────────────────────────────────────── */

function Rollout(): ReactNode {
  const stages: ReadonlyArray<{ icon: LucideIcon; title: string; body: string; tone: string }> = [
    { icon: Upload, title: '새 버전 준비', body: '공급사가 올려 둠 · 아직 어느 화면도 받지 않음', tone: 'border-slate-200 bg-white' },
    { icon: Monitor, title: '카나리', body: '고른 2~3대가 먼저 받음', tone: 'border-blue-200 bg-blue-50' },
    { icon: Hand, title: '현장 확인', body: '잘 도는지 하루쯤 지켜봄', tone: 'border-slate-200 bg-white' },
    { icon: MonitorPlay, title: '안정 승격', body: '나머지 화면 전부가 받음', tone: 'border-th-6 bg-th-2/60' },
  ];
  return (
    <div className="space-y-3">
      <ol className="flex flex-col items-stretch gap-1.5 sm:flex-row sm:items-center">
        {stages.map((stage, at) => {
          const Icon = stage.icon;
          return (
            <Fragment key={stage.title}>
              {at > 0 && <FlowArrow />}
              <li className={`flex flex-1 items-center gap-2.5 rounded-lg border px-3 py-2.5 ${stage.tone}`}>
                <Icon className="h-5 w-5 shrink-0 text-th-3" strokeWidth={1.9} />
                <span>
                  <span className="block text-[13px] font-semibold text-slate-900">{stage.title}</span>
                  <span className="block text-[11.5px] leading-snug text-slate-500">{stage.body}</span>
                </span>
              </li>
            </Fragment>
          );
        })}
      </ol>
      <p className="flex items-center justify-center gap-1.5 text-[12px] text-red-600">
        <ShieldAlert className="h-3.5 w-3.5" strokeWidth={2} />
        올라간 화면은 낮은 버전으로 되돌릴 수 없습니다 · 버전을 고정한 화면은 움직이지 않습니다
      </p>
    </div>
  );
}

/* ── 정비 모드 여는 법 ─────────────────────────────────────────────── */

function MaintenanceGesture(): ReactNode {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <div>
        <p className="mb-2 text-[12px] font-semibold text-slate-700">① 화면을 두 번</p>
        <div className="relative aspect-video overflow-hidden rounded-md border-4 border-slate-800 bg-slate-700">
          <div className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-th-5/40 ring-2 ring-th-5" />
          <Hand className="absolute left-1/2 top-1/2 h-6 w-6 text-white drop-shadow" strokeWidth={1.8} />
          <p className="absolute bottom-2 right-2 rounded bg-black/50 px-1.5 py-0.5 text-[10.5px] text-white">재생 중</p>
        </div>
        <p className="mt-1.5 text-[12px] text-slate-600">
          아무 곳이나 <strong className="font-semibold">빠르게 두 번</strong> — Windows 는 더블클릭 또는 F10
        </p>
      </div>
      <div>
        <p className="mb-2 text-[12px] font-semibold text-slate-700">② PIN 입력</p>
        <div className="flex aspect-video flex-col items-center justify-center rounded-md border border-slate-800 bg-slate-900 p-2">
          <p className="text-[11px] font-semibold text-white">PIN 을 입력하세요.</p>
          <p className="my-0.5 text-[15px] tracking-[0.3em] text-white">●●●●</p>
          <div className="flex gap-1">
            {['1', '2', '3'].map((digit) => (
              <span key={digit} className="w-7 rounded bg-slate-700 py-0.5 text-center text-[10.5px] text-white">
                {digit}
              </span>
            ))}
          </div>
        </div>
        <p className="mt-1.5 text-[12px] text-slate-600">
          정해 둔 PIN 이 없으면 <strong className="font-semibold">0000</strong>
        </p>
      </div>
      <div>
        <p className="mb-2 text-[12px] font-semibold text-slate-700">③ 정비 모드 (화면 전체)</p>
        {/*
          왼쪽 메뉴 · 오른쪽 내용 · 오른쪽 위 [×]. 왼쪽 아래 전원 아이콘은 2026-10-21 걷어냈다(앱 종료는 [전원·시작] 에만).
          기기 화면의 자리 관계를 그대로 옮긴다 — 설명서를 보고 기기 앞에 선 사람이
          그림과 다른 자리를 찾게 되면 그림이 없느니만 못하다.
        */}
        <div className="flex aspect-video rounded-md border border-slate-200 bg-white p-1.5">
          <div className="flex w-[34%] flex-col border-r border-slate-200 pr-1.5">
            <p className="text-[10.5px] font-semibold text-slate-900">정비 모드</p>
            <div className="mt-1 space-y-[3px]">
              {['개요', '재생', '서버 연결', '네트워크', '소프트웨어', '전원·시작'].map((item, at) => (
                <p
                  key={item}
                  className={`rounded px-1 py-[1px] text-[9.5px] ${at === 0 ? 'bg-slate-100 font-semibold text-slate-900' : 'text-slate-500'}`}
                >
                  {item}
                </p>
              ))}
            </div>
          </div>
          <div className="flex flex-1 flex-col pl-1.5">
            <div className="flex items-start justify-between">
              <p className="text-[10.5px] font-semibold text-slate-900">개요</p>
              <span className="rounded-md ring-2 ring-th-5 ring-offset-1">
                <span className="flex h-5 w-5 items-center justify-center rounded border border-slate-300 text-slate-600">
                  <X className="h-3 w-3" strokeWidth={2} />
                </span>
              </span>
            </div>
            <p className="mt-1 text-[9.5px] text-slate-500">이름 · 디바이스 ID · 상태 요약</p>
            <p className="mt-auto text-[10px] font-medium text-red-600">끝나면 꼭 [×] ↗</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── 플레이리스트 반복 ─────────────────────────────────────────────── */

function PlaylistLoop(): ReactNode {
  const items: ReadonlyArray<{ kind: string; title: string; seconds: number }> = [
    { kind: '이미지', title: '가을 프로모션', seconds: 8 },
    { kind: '템플릿', title: '이번 주 공지', seconds: 15 },
    { kind: '이미지', title: '매장 안내도', seconds: 8 },
  ];
  return (
    <div className="mx-auto max-w-xl">
      <ol className="flex flex-col items-stretch gap-1.5 sm:flex-row sm:items-center">
        {items.map((item, at) => (
          <Fragment key={item.title}>
            {at > 0 && <FlowArrow />}
            <li className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
              <p className="flex items-center justify-between text-[11px] text-slate-400">
                <span>
                  {at + 1} · {item.kind}
                </span>
                <span className="rounded bg-slate-100 px-1.5 font-medium text-slate-600">{item.seconds}초</span>
              </p>
              <p className="mt-1 text-[13px] font-medium text-slate-900">{item.title}</p>
            </li>
          </Fragment>
        ))}
      </ol>
      <div className="mx-6 mt-1 hidden h-4 rounded-b-lg border-x-2 border-b-2 border-dashed border-slate-300 sm:block" />
      <p className="mt-1.5 flex items-center justify-center gap-1 text-[12px] text-slate-500">
        <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} />
        끝까지 가면 처음으로 돌아가 계속 반복
      </p>
    </div>
  );
}

const FIGURES: Readonly<Record<FigureId, () => ReactNode>> = {
  'quick-start': QuickStart,
  'console-layout': ConsoleLayout,
  priority: Priority,
  'install-flow': InstallFlow,
  rollout: Rollout,
  'maintenance-gesture': MaintenanceGesture,
  'playlist-loop': PlaylistLoop,
};

export function Figure({ id }: { id: FigureId }): ReactNode {
  const Draw = FIGURES[id];
  return <Draw />;
}
